const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^(\d{4})-(\d{2})$/;

export const safeDate = (s: string, fallback: string): string =>
  ISO_DATE_RE.test(s) ? s : fallback;

export const fmtPct = (n: number, d: number): string => {
  if (d === 0) return "0.0%";
  return `${((n / d) * 100).toFixed(1)}%`;
};

export const pctVal = (n: number, d: number): number => (d === 0 ? 0 : (n / d) * 100);

export const fmtN = (n: number): string => n.toLocaleString();

export const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
};

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const fmtDateShort = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getUTCDate()).padStart(2, "0");
  const mon = SHORT_MONTHS[d.getUTCMonth()] ?? "";
  const yr = String(d.getUTCFullYear()).slice(-2);
  return `${day}-${mon}-${yr}`;
};

export function weeksBetween(startIso: string, endIso: string): number {
  const a = new Date(startIso).getTime();
  const b = new Date(endIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Infinity;
  return Math.floor((b - a) / (7 * 86400000));
}

// Floored elapsed days between two ISO timestamps. Use this for strict
// week-bucket membership (`daysBetween <= cap * 7`) — `weeksBetween` floors
// to whole weeks, which extends each bucket by 6 days vs the "by week N"
// reading users actually expect.
export function daysBetween(startIso: string, endIso: string): number {
  const a = new Date(startIso).getTime();
  const b = new Date(endIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Infinity;
  return Math.floor((b - a) / 86400000);
}

export function isoWeekKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

export function monthToRange(month: string): { start: string; end: string } {
  if (month === "all") return { start: "2020-01-01", end: "2099-01-01" };
  const match = month.match(MONTH_RE);
  if (!match) return { start: "2026-02-01", end: "2026-03-01" };
  const y = Number(match[1] ?? "2026");
  const m = Number(match[2] ?? "2");
  const start = `${y}-${String(m).padStart(2, "0")}-01`;
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  const end = `${ny}-${String(nm).padStart(2, "0")}-01`;
  return { start, end };
}

export const COUNTRY_GROUPS = ["NZ", "AU", "GB", "US", "Rest of World"] as const;
export type CountryGroup = (typeof COUNTRY_GROUPS)[number];

export function groupCountry(code: string | null | undefined): CountryGroup {
  if (code === "NZ" || code === "AU" || code === "GB" || code === "US") return code;
  return "Rest of World";
}

const COUNTRY_FLAG: Record<CountryGroup, string> = {
  NZ: "🇳🇿",
  AU: "🇦🇺",
  GB: "🇬🇧",
  US: "🇺🇸",
  "Rest of World": "🌐",
};

export function countryLabel(group: CountryGroup): string {
  return `${COUNTRY_FLAG[group]} ${group}`;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthLabel(month: string): string {
  if (month === "all") return "All time";
  const match = month.match(MONTH_RE);
  if (!match) return month;
  const yStr = match[1] ?? "";
  const m = Number(match[2] ?? "1");
  return `${MONTH_NAMES[m - 1] ?? "—"} ${yStr}`;
}

export const INCEPTION_MONTH = "2025-03";

export function recentMonths(from: Date = new Date()): string[] {
  const out: string[] = [];
  let yi = from.getUTCFullYear();
  let mi = from.getUTCMonth() + 1;
  const [iy, im] = INCEPTION_MONTH.split("-").map(Number) as [number, number];
  while (yi > iy || (yi === iy && mi >= im)) {
    out.push(`${yi}-${String(mi).padStart(2, "0")}`);
    mi -= 1;
    if (mi === 0) { mi = 12; yi -= 1; }
  }
  return out;
}

export const logrocketLink = (externalId: string): string =>
  `https://app.logrocket.com/tqpte0/marloo?filters=%5B%7B%22id%22%3A%22r1vvC3nkge%22%2C%22type%22%3A%22userID%22%2C%22operator%22%3A%7B%22name%22%3A%22is%22%2C%22type%22%3A%22IS%22%2C%22hasStrings%22%3Atrue%2C%22autocompleteEnabled%22%3Atrue%7D%2C%22strings%22%3A%5B%22${externalId}%22%5D%7D%5D&persistForm=true&interval=1d&range=1M&`;

export const mixpanelLink = (externalId: string): string =>
  `https://eu.mixpanel.com/project/3599744/view/4099528/app/profile#distinct_id=${externalId}`;

export function previousMonth(from: Date = new Date()): string {
  const y = from.getUTCFullYear();
  const m = from.getUTCMonth() + 1;
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  return `${py}-${String(pm).padStart(2, "0")}`;
}
